import React, { useState } from "react"
import {
  Card,
  CardBody,
  Table,
  Badge,
  Button,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap"
import {
  CreateProductionOrder,
  UpdateProductionOrder,
  DeleteProductionOrder,
  UploadCPSAttachment,
} from "helpers/backend_helper"
import { useLoading } from "helpers/custom_hooks"
import { displayMessage } from "helpers/misc_helpers"
import moment from "moment"

const CPSOrdersTable = ({ entry, myUser, productionOrders, reload, shipments = [] }) => {
  const setLoading = useLoading()
  const [isTableLoading, setIsTableLoading] = useState(false)
  const [showNewOrderModal, setShowNewOrderModal] = useState(false)
  const [showCompleteOrderModal, setShowCompleteOrderModal] = useState(false)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [newOrder, setNewOrder] = useState({
    quantity: "",
    deliveryDate: "",
    notes: "",
  })
  const [completeOrderData, setCompleteOrderData] = useState({
    trackingNumber: "",
    certificate: null,
    shippingService: "",
  })
  const [error, setError] = useState(null)

  // Calculate available stock for new production orders
  const calculateAvailableStock = () => {
    // Sum of delivered shipments
    const deliveredQuantity = shipments
      .filter(shipment => shipment.delivered)
      .reduce((total, shipment) => total + (shipment.quantity || 0), 0)

    // Sum of completed production orders
    const completedQuantity = productionOrders
      .filter(order => order.certificateURL)
      .reduce((total, order) => total + (order.quantity || 0), 0)

    // Sum of subrequest quantities
    const subRequestQuantity = (entry?.subRequests || [])
      .reduce((total, subRequest) => total + (subRequest.quantity || 0), 0)

    // Sum of all production orders (including pending ones)
    const allProductionOrdersQuantity = productionOrders
      .reduce((total, order) => total + (order.quantity || 0), 0)

    // Available stock = initial + delivered - completed - subrequests - pending orders
    const currentStock = (entry?.quantity || 0) + deliveredQuantity - completedQuantity - subRequestQuantity
    const availableForOrders = currentStock - (allProductionOrdersQuantity - completedQuantity)
    
    return Math.max(0, availableForOrders)
  }

  const availableStock = calculateAvailableStock()

  // Demo orders for annual quantity
  // Transform API production orders data to match frontend expectations
  const transformedOrders = productionOrders.map(order => ({
    ...order,
    status: order.certificateURL ? "Completed" : "In Production",
    certificateUploaded: !!order.certificateURL,
    certificateUrl: order.certificateURL,
    dateCreated: order.dateCreated
      ? moment(order.dateCreated).format("DD.MM.YYYY")
      : "",
  }))

  const handleUploadCertificate = async (orderId, file) => {
    if (!file) return

    setIsTableLoading(true)
    setLoading(true)
    try {
      // Upload the certificate file
      const formData = new FormData()
      formData.append("file", file)
      formData.append("id", orderId)
      const uploadResult = await UploadCPSAttachment(formData)

      // Update the production order with certificate URL
      const order = transformedOrders.find(o => o.id === orderId)
      if (order) {
        const updateData = {
          id: orderId,
          deliveryDate: order.deliveryDate,
          quantity: order.quantity,
          trackingNumber: order.trackingNumber,
          certificateURL: uploadResult?.url,
          notes: order.notes || "",
        }

        await UpdateProductionOrder(updateData)

        // Reload data to show updated order
        if (reload) {
          await reload()
        }
      }
    } catch (error) {
      console.error("Error uploading certificate:", error)
      displayMessage("Error uploading certificate. Please try again.", setError)
    } finally {
      setLoading(false)
      setIsTableLoading(false)
    }
  }

  const handleUpdateTracking = async orderId => {
    const trackingNumber = prompt("Enter tracking number:")
    if (!trackingNumber) return

    setIsTableLoading(true)
    setLoading(true)
    try {
      const order = transformedOrders.find(o => o.id === orderId)
      if (order) {
        const updateData = {
          id: orderId,
          deliveryDate: order.deliveryDate,
          quantity: order.quantity,
          trackingNumber: trackingNumber,
          certificateURL: order.certificateURL,
          notes: order.notes || "",
        }

        await UpdateProductionOrder(updateData)

        // Reload data to show updated order
        if (reload) {
          await reload()
        }
      }
    } catch (error) {
      console.error("Error updating tracking:", error)
      displayMessage("Error updating tracking. Please try again.", setError)
    } finally {
      setLoading(false)
      setIsTableLoading(false)
    }
  }

  const handleCreateNewOrder = async () => {
    if (!newOrder.quantity || !newOrder.deliveryDate) {
      displayMessage("Please fill in all required fields", setError)
      return
    }

    const orderQuantity = parseInt(newOrder.quantity)
    
    // Check if there's enough stock available
    if (orderQuantity > availableStock) {
      displayMessage(
        `Not enough stock available. You can order up to ${availableStock.toLocaleString()} pcs.`,
        setError
      )
      return
    }

    setLoading(true)
    setIsTableLoading(true)
    try {
      const orderData = {
        cpsRequestID: entry.id,
        deliveryDate: newOrder.deliveryDate,
        quantity: orderQuantity,
        trackingNumber: "",
        shippingService: "",
        certificateURL: null,
        notes: newOrder.notes || "",
      }

      await CreateProductionOrder(orderData)

      setShowNewOrderModal(false)
      setNewOrder({
        quantity: "",
        deliveryDate: "",
        notes: "",
      })

      // Reload data to show new order
      if (reload) {
        await reload()
      }
    } catch (error) {
      console.error("Error creating production order:", error)
      displayMessage(
        "Error creating production order. Please try again.",
        setError
      )
    } finally {
      setLoading(false)
      setIsTableLoading(false)
    }
  }

  const handleCompleteOrder = order => {
    setSelectedOrder(order)
    setCompleteOrderData({
      trackingNumber: "",
      certificate: null,
    })
    setShowCompleteOrderModal(true)
  }

  const handleShowDetails = order => {
    setSelectedOrder(order)
    setShowDetailsModal(true)
  }

  const handleSubmitCompleteOrder = async () => {
    if (completeOrderData.trackingNumber && completeOrderData.certificate) {
      setLoading(true)
      setIsTableLoading(true)
      try {
        // Upload the certificate file
        const formData = new FormData()
        formData.append("file", completeOrderData.certificate)
        formData.append("id", selectedOrder.id)
        const uploadResult = await UploadCPSAttachment(formData)

        // Update the production order with tracking number, certificate, and dates
        const updateData = {
          id: selectedOrder.id,
          deliveryDate: selectedOrder.deliveryDate,
          quantity: selectedOrder.quantity,
          trackingNumber: completeOrderData.trackingNumber,
          shippingService: completeOrderData.shippingService || "",
          certificateURL: uploadResult?.url,
          notes: selectedOrder.notes || "",
        }

        await UpdateProductionOrder(updateData)

        setShowCompleteOrderModal(false)
        setSelectedOrder(null)
        setCompleteOrderData({
          trackingNumber: "",
          certificate: null,
          shippingService: "",
        })

        // Reload data to show updated order
        if (reload) {
          await reload()
        }
      } catch (error) {
        console.error("Error completing order:", error)
        displayMessage("Error completing order. Please try again.", setError)
      } finally {
        setLoading(false)
        setIsTableLoading(false)
      }
    } else {
      alert("Please fill in all required fields")
    }
  }

  const getStatusColor = status => {
    switch (status) {
      case "Completed":
        return "success"
      case "In Production":
        return "warning"
      case "Planned":
        return "info"
      default:
        return "secondary"
    }
  }

  // Only show for annual needs
  if (entry?.needType !== "annual") {
    return null
  }

  return (
    <>
      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Production Orders</h5>
            <div className="d-flex align-items-center gap-2">
              <Badge color="primary" size="sm">
                {transformedOrders.length} Orders
              </Badge>
              <Badge 
                color={availableStock > 0 ? "success" : "danger"} 
                size="sm"
                title="Available stock for new orders"
              >
                {availableStock.toLocaleString()} pcs available
              </Badge>
              <Button
                color="success"
                size="sm"
                onClick={() => {
                  setShowNewOrderModal(true)
                  setError(null)
                }}
                title={availableStock > 0 ? "Create New Order" : "No stock available"}
                disabled={isTableLoading || availableStock <= 0}
              >
                <i className="bx bx-plus me-1"></i>
                New Order
              </Button>
            </div>
          </div>

          {isTableLoading && (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="mt-2 text-muted">
                Updating production orders...
              </div>
            </div>
          )}

          <div
            className="table-responsive"
            style={{ opacity: isTableLoading ? 0.5 : 1 }}
          >
            <Table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>Date</th>
                  <th>Quantity</th>
                  <th>Tracking</th>
                  <th>Certificate</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {transformedOrders.map(order => (
                  <tr key={order.id}>
                    <td>
                      <div>
                        <strong className="text-muted">
                          {order.dateCreated}
                        </strong>
                      </div>
                    </td>
                    <td>
                      <div>
                        <strong>{order.quantity.toLocaleString()} pcs</strong>
                      </div>
                    </td>
                    <td>
                      {order.trackingNumber ? (
                        <span className="text-success">
                          <i className="bx bx-check-circle me-1"></i>
                          {order.trackingNumber}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      {order.certificateUploaded ? (
                        <a
                          href={order.certificateUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-success"
                        >
                          <i className="bx bx-download me-1"></i>
                          Download
                        </a>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td style={{ width: "100px" }}>
                      <div className="d-flex gap-1">
                        <Button
                          color="info"
                          size="sm"
                          onClick={() => handleShowDetails(order)}
                          title="View Details"
                        >
                          <i className="bx bx-show"></i>
                        </Button>
                        {order.status === "In Production" &&
                          myUser?.company?.id === entry?.cpsLabID && (
                            <Button
                              color="success"
                              size="sm"
                              onClick={() => handleCompleteOrder(order)}
                              title="Complete Order"
                              disabled={isTableLoading}
                            >
                              <i className="bx bx-check"></i>
                            </Button>
                          )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Action buttons for CPS Lab */}
        </CardBody>
      </Card>

      {/* New Order Modal */}
      <Modal
        isOpen={showNewOrderModal}
        toggle={() => {
          setShowNewOrderModal(false)
          setError(null)
        }}
      >
        <ModalHeader toggle={() => {
          setShowNewOrderModal(false)
          setError(null)
        }}>
          Create New Production Order
        </ModalHeader>
        <ModalBody>
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}
          <div className="alert alert-info mb-3">
            <i className="bx bx-info-circle me-2"></i>
            <strong>Available Stock:</strong> {availableStock.toLocaleString()} pcs
          </div>
          <Form>
            <FormGroup>
              <Label for="quantity">Quantity *</Label>
              <Input
                id="quantity"
                type="number"
                value={newOrder.quantity}
                onChange={e =>
                  setNewOrder({ ...newOrder, quantity: e.target.value })
                }
                placeholder="Enter quantity"
                max={availableStock}
                min="1"
                required
                invalid={newOrder.quantity && parseInt(newOrder.quantity) > availableStock}
              />
              {newOrder.quantity && parseInt(newOrder.quantity) > availableStock && (
                <small className="text-danger">
                  Quantity exceeds available stock ({availableStock.toLocaleString()} pcs)
                </small>
              )}
            </FormGroup>
            <FormGroup>
              <Label for="deliveryDate">Delivery Date *</Label>
              <Input
                id="deliveryDate"
                type="date"
                value={newOrder.deliveryDate}
                onChange={e =>
                  setNewOrder({ ...newOrder, deliveryDate: e.target.value })
                }
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for="notes">Notes</Label>
              <Input
                id="notes"
                type="textarea"
                value={newOrder.notes}
                onChange={e =>
                  setNewOrder({ ...newOrder, notes: e.target.value })
                }
                placeholder="Optional notes for this order"
                rows="3"
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => {
            setShowNewOrderModal(false)
            setError(null)
          }}>
            Cancel
          </Button>
          <Button 
            color="success" 
            onClick={handleCreateNewOrder}
            disabled={!newOrder.quantity || !newOrder.deliveryDate || parseInt(newOrder.quantity) > availableStock}
          >
            Create Order
          </Button>
        </ModalFooter>
      </Modal>

      {/* Complete Order Modal */}
      <Modal
        isOpen={showCompleteOrderModal}
        toggle={() => setShowCompleteOrderModal(false)}
      >
        <ModalHeader toggle={() => setShowCompleteOrderModal(false)}>
          Complete Order #{selectedOrder?.id}
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="trackingNumber">Tracking Number *</Label>
              <Input
                id="trackingNumber"
                type="text"
                value={completeOrderData.trackingNumber}
                onChange={e =>
                  setCompleteOrderData({
                    ...completeOrderData,
                    trackingNumber: e.target.value,
                  })
                }
                placeholder="Enter tracking number"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for="shippingService">Shipping Service</Label>
              <Input
                id="shippingService"
                type="text"
                value={completeOrderData.shippingService}
                onChange={e =>
                  setCompleteOrderData({
                    ...completeOrderData,
                    shippingService: e.target.value,
                  })
                }
                placeholder="e.g., DHL, FedEx, UPS"
              />
            </FormGroup>
            <FormGroup>
              <Label for="certificate">Production Certificate *</Label>
              <Input
                id="certificate"
                type="file"
                onChange={e =>
                  setCompleteOrderData({
                    ...completeOrderData,
                    certificate: e.target.files[0],
                  })
                }
                accept=".pdf,.doc,.docx"
                required
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setShowCompleteOrderModal(false)}
          >
            Cancel
          </Button>
          <Button
            color="success"
            onClick={handleSubmitCompleteOrder}
            disabled={
              !completeOrderData.trackingNumber ||
              !completeOrderData.certificate
            }
          >
            Complete Order
          </Button>
        </ModalFooter>
      </Modal>

      {/* Order Details Modal */}
      <Modal
        isOpen={showDetailsModal}
        toggle={() => setShowDetailsModal(false)}
        size="lg"
      >
        <ModalHeader toggle={() => setShowDetailsModal(false)}>
          Production Order Details #{selectedOrder?.id}
        </ModalHeader>
        <ModalBody>
          {selectedOrder && (
            <div className="row">
              <div className="col-md-6">
                <h6 className="text-muted mb-3">Order Information</h6>
                <table className="table table-sm">
                  <tbody>
                    <tr>
                      <td>
                        <strong>Order ID:</strong>
                      </td>
                      <td>{selectedOrder.id}</td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Quantity:</strong>
                      </td>
                      <td>{selectedOrder.quantity?.toLocaleString()} pcs</td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Delivery Date:</strong>
                      </td>
                      <td>
                        {selectedOrder.deliveryDate
                          ? moment(selectedOrder.deliveryDate).format(
                              "DD.MM.YYYY"
                            )
                          : "-"}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Status:</strong>
                      </td>
                      <td>
                        <Badge
                          color={
                            selectedOrder.status === "Completed"
                              ? "success"
                              : "warning"
                          }
                        >
                          {selectedOrder.status}
                        </Badge>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="col-md-6">
                <h6 className="text-muted mb-3">Shipping & Completion</h6>
                <table className="table table-sm">
                  <tbody>
                    <tr>
                      <td>
                        <strong>Tracking Number:</strong>
                      </td>
                      <td>{selectedOrder.trackingNumber || "-"}</td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Shipping Service:</strong>
                      </td>
                      <td>{selectedOrder.shippingService || "-"}</td>
                    </tr>

                    <tr>
                      <td>
                        <strong>Notes:</strong>
                      </td>
                      <td>{selectedOrder.notes}</td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Certificate:</strong>
                      </td>
                      <td>
                        {selectedOrder.certificateURL ? (
                          <a
                            href={selectedOrder.certificateURL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-primary"
                          >
                            <i className="bx bx-download me-1"></i>
                            Download
                          </a>
                        ) : (
                          <span className="text-muted">No certificate</span>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default CPSOrdersTable
